import { useMemo, useRef, useState } from 'react';

import { useModal } from '@bitstopco/bitstop-theme';
import type {
  ApiListingRequest,
  Columns,
  TableToolbarFilterConfig,
  TableWrapperRef,
} from '@bitstopco/bitstop-theme';
import { TableWrapper } from '@bitstopco/bitstop-theme';
import { skipToken } from '@reduxjs/toolkit/query';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack } from '@mui/material';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import MachineMachineTabTitle from '@/components/machine/tabs/machine/MachineMachineTabTitle';
import ActionsPopover from '@/components/ui/ActionPopover';
import CardTable from '@/components/ui/card/CardTable';

import { DEFAULT_PAGINATION } from '@/constants/api';
import { GLOBAL_CASH_LOGS_COLUMNS } from '@/constants/app/machines';

import { appendToFilters } from '@/helpers/api';
import {
  DEPOSIT_OUTCOME_STATUSES,
  canSetDepositOutcome,
  cashLogStatusLabel,
} from '@/helpers/cashLogs';

import {
  useConfirmCashLogMutation,
  useGetCashLogsExportMutation,
  useGetCashLogsQuery,
  useSetCashLogStatusMutation,
} from '@/store/api/services/machines/cashlogs';

import type { MachinesCashLog } from '@/types';
import type { CashLogStatus } from '@/types/machine';

/** Per-machine cash logs filters: Status + Provenance (date range is built in). */
const FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    type: 'select',
    property: 'status',
    placeholder: 'Status',
    label: 'Status',
    showFooter: true,
    hideApplyButton: true,
    clearText: 'Clear filter',
    options: [
      { value: 'provisional', label: 'Provisional' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'invalidated', label: 'Invalidated' },
      { value: 'deposited', label: 'Deposited' },
      { value: 'deposited_over', label: 'Deposited Over' },
      { value: 'deposited_under', label: 'Deposited Under' },
      { value: 'voided', label: 'Voided' },
    ],
  },
  {
    type: 'select',
    property: 'provenance',
    placeholder: 'Provenance',
    label: 'Provenance',
    showFooter: true,
    hideApplyButton: true,
    clearText: 'Clear filter',
    // `heartbeat_inferred` is omitted until backend confirms it is still populated.
    options: [
      { value: 'machine_reported', label: 'Machine' },
      { value: 'manual', label: 'Manual' },
    ],
  },
];

/**
 * Per-machine Cash Logs tab: the row-level action surface for the cash team.
 * Confirm is a one-click command; Invalidate and Create open their modals.
 */
const MachineCashLogs = () => {
  const { id: machineId = '0' } = useParams();
  const { show } = useModal();
  const navigate = useNavigate();

  const [payload, setPayload] = useState<ApiListingRequest>();
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const tableRef = useRef<TableWrapperRef>(null);

  const [exportMutation] = useGetCashLogsExportMutation();
  const [confirmCashLog] = useConfirmCashLogMutation();
  const [setCashLogStatus] = useSetCashLogStatusMutation();

  const computedPayload = useMemo(() => {
    if (!payload) return skipToken;
    return appendToFilters(payload, [{ operator: 'equal', property: 'atm_id', value: +machineId }]);
  }, [payload, machineId]);

  const {
    data: {
      data = [],
      meta: { pagination = DEFAULT_PAGINATION } = {},
    } = {},
    isFetching,
  } = useGetCashLogsQuery(computedPayload);

  const handleConfirm = async (row: MachinesCashLog) => {
    setConfirmingId(row.id);
    try {
      // Confirm needs no reason. The mutation invalidates the list, so the row
      // refetches with its new status.
      await confirmCashLog({ id: row.id }).unwrap();
      toast.success(`Clear #${row.id} confirmed`);
    } catch {
      toast.error(`Could not confirm clear #${row.id}. Please try again.`);
    } finally {
      setConfirmingId(null);
    }
  };

  const handleSetStatus = async (row: MachinesCashLog, status: CashLogStatus) => {
    setUpdatingId(row.id);
    try {
      await setCashLogStatus({ id: row.id, status }).unwrap();
      toast.success(
        status === 'provisional'
          ? `Clear #${row.id} reverted to provisional`
          : `Clear #${row.id} set to ${cashLogStatusLabel(status).toLowerCase()}`,
      );
    } catch {
      toast.error(`Could not update clear #${row.id}. Please try again.`);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleInvalidate = (row: MachinesCashLog) => show('cash-log-invalidate', { id: row.id });

  const handleCreateManual = () => show('cash-log-create-manual', { atmId: +machineId });

  const handleExport = (type: string) => {
    if (computedPayload === skipToken) return;
    tableRef.current?.getExportFile({
      type,
      overridePayload: computedPayload,
      mutation: exportMutation,
      fileName: 'Nonce-Cash-Logs-Report',
    });
  };

  // Base global columns minus Machine/Location (single-machine context) + Actions.
  const columns = useMemo<Columns<MachinesCashLog>>(
    () => ({
      ...GLOBAL_CASH_LOGS_COLUMNS,
      items: [
        ...GLOBAL_CASH_LOGS_COLUMNS.items.filter(
          (c) => c.value !== 'atm_id' && c.value !== 'location_id',
        ),
        {
          label: 'Actions',
          hideSort: true,
          valueMapper: (_, row) => {
            const busy = confirmingId === row.id || updatingId === row.id;
            const actions = [
              {
                label: 'Confirm',
                action: 'cash-log-confirm',
                disabled: row.status !== 'provisional' || busy,
                onClick: () => handleConfirm(row),
              },
              {
                label: 'Set to Provisional',
                action: 'cash-log-set-provisional',
                disabled: row.status !== 'confirmed' || busy,
                onClick: () => handleSetStatus(row, 'provisional'),
              },
              ...DEPOSIT_OUTCOME_STATUSES.map((status) => ({
                label: `Set to ${cashLogStatusLabel(status)}`,
                action: `cash-log-set-${status.replaceAll('_', '-')}`,
                disabled: !canSetDepositOutcome(row.status, status) || busy,
                onClick: () => handleSetStatus(row, status),
              })),
              {
                label: 'Invalidate',
                action: 'cash-log-invalidate',
                disabled: row.status === 'invalidated',
                onClick: () => handleInvalidate(row),
              },
            ];
            const items = actions.map(({ label, disabled, action }) => ({
              label,
              disabled,
              action,
            }));
            const handleClick = (index: number) => actions[index]?.onClick();
            return (
              <Stack direction="row" justifyContent="flex-end">
                <ActionsPopover
                  items={items}
                  onClick={handleClick}
                  buttonProps={{ size: 'small', variant: 'outlined' }}
                />
              </Stack>
            );
          },
        },
      ],
    }),
    [confirmingId, updatingId],
  );

  return (
    <Box mt={2}>
      <CardTable data-test="cash-logs-table" sx={{ mt: 0 }}>
        <MachineMachineTabTitle
          title="Cash Logs"
          sxContainer={{ pt: 2, px: 2 }}
          sxDivider={{ mb: '4px' }}
          renderButton={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateManual}
              data-test="cash-log-create-manual-button"
            >
              Create Manual Clear
            </Button>
          }
        />
        <TableWrapper<MachinesCashLog>
          data={data}
          ref={tableRef}
          dateRangeFilterKey="created_at"
          isLoading={isFetching || !payload}
          columns={columns}
          filterConfigs={FILTERS_CONFIG}
          totalPages={pagination?.total_pages}
          totalItems={pagination?.total_items}
          onChange={setPayload}
          onActionMenuItemClick={handleExport}
          onRowClick={(row) => navigate(`/cash-log/${row.id}`)}
          tableProps={{
            placeholder: {
              empty: {
                title: 'No cash logs found',
                content:
                  "We can't find any cash log that matches your search, please try a different request",
              },
            },
          }}
        />
      </CardTable>
    </Box>
  );
};

export default MachineCashLogs;
